import type { Rng } from '@/core/Rng';
import type { SceneryArrays } from './types';

/** `at(lateral)` from ZoneCtx — a world point `lateral` units out from the
 *  centreline on the current side. */
type At = (lateral: number) => [number, number, number];

export interface ForestOpts {
  /** 'pine' → conifer/umbrella cones (a.pines); 'tree' → round-blob canopy (a.trees). */
  kind: 'pine' | 'tree';
  /** Foliage colours to pick from per tree. */
  colors: readonly number[];
  /** Trees placed in THIS module, [min, max] (capped by the number of gap slots). */
  count: [number, number];
  /** Lateral clearance BEYOND the guardrail (hw) before the nearest trunk, so
   *  even the front row's canopy clears the road. Default 6. */
  inner?: number;
  /** Depth of the tree band out past `inner` (the cascade runs across this). Default 34. */
  depth?: number;
  /** Minimum lateral spacing between trees — the GAP you see the ball through. Default 8. */
  gap?: number;
  /** Canopy scale at the NEAR edge — small, so the front row never walls the view. Default 0.8. */
  sizeNear?: number;
  /** Canopy scale at the FAR edge — bigger, so the forest rises behind. Default 1.5. */
  sizeFar?: number;
}

/**
 * scatterForest (M67, owner) — a "forest you can SEE THROUGH". The old forest
 * zones dumped a random clump of full-size trees starting 2.5u outside the
 * guardrail, so canopies overhung the road and stacked module-after-module into
 * a solid wall that hid the ball. This lays each module's trees as INDIVIDUAL,
 * CASCADED trees instead:
 *   - a clear inner margin (nothing overhangs the driving surface),
 *   - staggered DEPTH slots a fixed `gap` apart, so two trees never crowd the
 *     same line of sight — you can see (and the ball can be seen) BETWEEN them,
 *   - size rising with distance (small in front, tall behind) so it reads as a
 *     forest receding into depth, not a curtain right at the rail.
 * Deterministic — uses ONLY the zone's main `rng`, so every other prop stays put.
 */
export function scatterForest(
  rng: Rng,
  hw: number,
  at: At,
  a: SceneryArrays,
  o: ForestOpts,
): void {
  const inner = hw + (o.inner ?? 6);
  const depth = o.depth ?? 34;
  const gap = o.gap ?? 8;
  const sizeNear = o.sizeNear ?? 0.8;
  const sizeFar = o.sizeFar ?? 1.5;
  // Discrete depth rows a `gap` apart; a tree lands in one slot, so trunks keep
  // their spacing and the see-through gaps survive.
  const slots = Math.max(1, Math.floor(depth / gap) + 1);
  const n = Math.min(rng.int(o.count[0], o.count[1]), slots);
  const used = new Set<number>();
  for (let k = 0; k < n; k++) {
    // Claim an unused slot (n <= slots, so this always finds one).
    let slot = rng.int(0, slots - 1);
    while (used.has(slot)) slot = (slot + 1) % slots;
    used.add(slot);
    const t = slots > 1 ? slot / (slots - 1) : 0; // 0 = nearest row, 1 = farthest
    const lat = inner + t * depth + rng.range(-gap * 0.3, gap * 0.3);
    const s = Math.max(0.5, sizeNear + t * (sizeFar - sizeNear) + rng.range(-0.08, 0.12));
    const [x, y, z] = at(lat);
    const color = rng.pick(o.colors);
    if (o.kind === 'pine') a.pines.push({ x, y, z, rotY: 0, s, color });
    else a.trees.push({ x, y, z, s, color });
  }
}
